import { stringify } from 'query-string'
import { ListRequestParams } from 'modules/domain/types'
import { TotalsData, UsContract, UsContractPositionTypes, UsContractStatuses } from 'modules/domain/usContracts/types'
import { FC, useCallback, useEffect, useState } from 'react'
import { UsContractDrawer } from '../Drawer/UsContractDrawer'
import { Column } from './Column'
import { useTranslation } from 'react-i18next'
import { endpoints } from 'modules/endpoints'
import { useSingleEntity, useTableData } from 'modules/domain/common/hooks'
import { CardsBoard } from 'views/components/Board/CardsBoard'
import { PositionsBlock } from '../Positions/PositionsBlock'
import { Progress } from '@agro-club/agroclub-shared'

type Props = {
  listRequestParams: ListRequestParams
  updateCommonFilterState: (filterObj: Record<string, any>) => void
  isFiltersLoaded: boolean
  setSelectedContractId: (id: number | undefined) => void
  selectedContractId?: number
  isClosed?: boolean
}

export const DEFAULT_MARGIN = 265

export const OpenBoard: FC<Props> = ({
  listRequestParams,
  updateCommonFilterState,
  selectedContractId,
  setSelectedContractId,
  isFiltersLoaded,
}) => {
  const { t } = useTranslation('usContracts')
  const [selectedContract, setSelectedContract] = useState<UsContract | undefined>(undefined)
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedContractId)
  const [totalsFormated, setTotalsFomated] = useState<Record<string, number | undefined>>({})
  const [positionsListRequestParams, setPositionsListRequestParams] = useState<ListRequestParams>({
    ...listRequestParams,
    filter: { ...listRequestParams.filter, status: UsContractStatuses.signed },
  })

  useEffect(() => {
    setPositionsListRequestParams(listRequestParams)
  }, [listRequestParams, isFiltersLoaded])
  // it is important to keep minumum deps here for performance reasons
  // all side efffects should be handled in useEffect
  const onContractClick = useCallback(
    (contract: UsContract) => {
      // we need have separate  state for selectedContractId if need just selected card after page reload
      // selectedContract - for details drawer
      setSelectedContract(contract)
      setSelectedContractId(contract.id)
      setIsDrawerOpen(true)
    },
    [setSelectedContractId],
  )

  const {
    data: newContracts,
    progress: newProgress,
    refetch: refetchNew,
  } = useTableData<UsContract>(endpoints.usContractsActualNew, listRequestParams, isFiltersLoaded)
  const {
    data: signedContracts,
    progress: signedProgress,
    refetch: refetchSigned,
  } = useTableData<UsContract>(endpoints.usContractsActualSigned, listRequestParams, isFiltersLoaded)

  const totalsUrl = `${endpoints.usContactsTotal()}?${stringify(listRequestParams.filter)}`
  const [totalProgress, totals, totalsRefetch] = useSingleEntity<TotalsData>(totalsUrl)

  useEffect(() => {
    setTotalsFomated({
      [UsContractStatuses.new]:
        totals?.contracts.find((c) => c.status === UsContractStatuses.new)?.remaining_quantity ?? 0,
      [UsContractStatuses.signed]:
        totals?.contracts.find((c) => c.status === UsContractStatuses.signed)?.remaining_quantity ?? 0,
      [UsContractPositionTypes.short]:
        totals?.positions.find((p) => p.position_type === UsContractPositionTypes.short)?.remaining_quantity ?? 0,
      [UsContractPositionTypes.long]:
        totals?.positions.find((p) => p.position_type === UsContractPositionTypes.long)?.remaining_quantity ?? 0,
    })
  }, [totals])

  const refetchAll = useCallback(
    async (affected?: UsContractStatuses | 'allTabs') => {
      if (affected === 'allTabs') {
        updateCommonFilterState((prev) => ({ ...prev }))
        return
      }
      if (affected === UsContractStatuses.new) {
        await refetchNew()
      } else if (affected === UsContractStatuses.signed) {
        await refetchSigned()
      } else {
        const newPromise = refetchNew()
        const signedPromise = refetchSigned()
        setPositionsListRequestParams((prev) => ({ ...prev }))
        await Promise.all([newPromise, signedPromise])
      }
      totalsRefetch()
    },
    [refetchNew, refetchSigned, totalsRefetch, updateCommonFilterState],
  )

  // update selected contract when data changes
  useEffect(() => {
    if (newProgress == Progress.WORK || signedProgress == Progress.WORK) {
      return
    }
    if (selectedContractId) {
      let selected = newContracts?.find((contract) => contract.id === selectedContractId)
      if (!selected) {
        selected = signedContracts?.find((contract) => contract.id === selectedContractId)
      }
      setSelectedContract(selected)
    }
  }, [newContracts, newProgress, selectedContractId, signedContracts, signedProgress])

  return (
    <CardsBoard>
      <Column
        onCardClick={onContractClick}
        selectedContractId={selectedContractId}
        data={newContracts}
        progress={newProgress}
        total={totalsFormated.new}
        totalProgress={totalProgress}
        topMargin={DEFAULT_MARGIN}
        title={t('contractStatuses.new')}
      ></Column>
      <Column
        onCardClick={onContractClick}
        selectedContractId={selectedContractId}
        data={signedContracts}
        progress={signedProgress}
        total={totalsFormated.signed}
        totalProgress={totalProgress}
        topMargin={DEFAULT_MARGIN}
        title={t('contractStatuses.signed')}
      ></Column>
      <PositionsBlock
        totals={totalsFormated}
        totalProgress={totalProgress}
        listRequestParams={positionsListRequestParams}
      />
      {isDrawerOpen && selectedContract && (
        // selectedContract.id === selectedContractId - need to make sure selectedContract was refreshed
        <UsContractDrawer contract={selectedContract} refetch={refetchAll} onClose={() => setIsDrawerOpen(false)} />
      )}
    </CardsBoard>
  )
}
